import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  User,
  Users,
  Lock,
  Bell,
  Shield,
  CheckCircle,
  AlertCircle,
  Trash2,
} from 'lucide-react';
import { ChangePasswordCard } from '../components/ChangePasswordCard';
import { TeamManagementTab } from '../components/TeamManagementTab';
import { Modal, ModalHeader, ModalTitle, ModalBody, ModalFooter } from '@/components/ui/modal';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/common/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { useAuthStore } from '@/stores/authStore';
import api from '@/lib/axios';
import { API_ENDPOINTS } from '@/constants/api-endpoints';

const profileSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  phone: z.string().optional(),
});

type ProfileFormData = z.infer<typeof profileSchema>;

function ProfileTab() {
  const { user, setUser } = useAuthStore();
  const [saveSuccess, setSaveSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
    },
  });

  const { mutate: updateProfile, isPending, isError, error } = useMutation({
    mutationFn: async (data: ProfileFormData) => {
      const response = await api.put(API_ENDPOINTS.AUTH.PROFILE, data);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      // Use the values we just submitted, not the raw response — the API response
      // is interceptor-wrapped ({ success, data, timestamp }), so spreading it
      // never overwrites name/email/phone and the UI keeps showing the old name.
      if (user) {
        setUser({ ...user, ...variables });
      }
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    },
  });

  return (
    <div className="space-y-6">
      {/* Profile Avatar */}
      <Card>
        <CardContent className="flex items-center gap-6 p-6">
          <Avatar alt={user?.name} size="xl" />
          <div>
            <h3 className="text-lg font-semibold">{user?.name}</h3>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
            <Badge variant="outline" className="mt-2 capitalize">
              {user?.role?.replace('_', ' ')}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Profile Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Personal Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={handleSubmit((data) => updateProfile(data))}
            className="space-y-4"
          >
            {saveSuccess && (
              <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 p-3 rounded-lg">
                <CheckCircle className="h-4 w-4" />
                Profile updated successfully.
              </div>
            )}
            {isError && (
              <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 p-3 rounded-lg">
                <AlertCircle className="h-4 w-4" />
                {(error as any)?.response?.data?.message || 'Failed to update profile.'}
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="name" required>
                  Full Name
                </Label>
                <Input
                  id="name"
                  placeholder="Your full name"
                  error={errors.name?.message}
                  {...register('name')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" required>
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  error={errors.email?.message}
                  {...register('email')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+91 9000000000"
                  error={errors.phone?.message}
                  {...register('phone')}
                />
              </div>
            </div>

            <div className="flex justify-end">
              <Button type="submit" disabled={!isDirty || isPending} isLoading={isPending}>
                Save Changes
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function SecurityTab() {
  return (
    <div className="space-y-6">
      <ChangePasswordCard />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Security Info
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between py-2 border-b">
            <div>
              <p className="font-medium text-sm">Two-Factor Authentication</p>
              <p className="text-xs text-muted-foreground">Add an extra layer of security</p>
            </div>
            <Badge variant="secondary">Coming soon</Badge>
          </div>
          <div className="flex items-center justify-between py-2">
            <div>
              <p className="font-medium text-sm">Active Sessions</p>
              <p className="text-xs text-muted-foreground">Manage where you're logged in</p>
            </div>
            <Badge variant="secondary">Coming soon</Badge>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function NotificationsTab() {
  const [settings, setSettings] = useState({
    newBookings: true,
    driverAlerts: true,
    paymentAlerts: true,
    kycReviews: true,
    systemAlerts: false,
    weeklyReports: true,
  });

  const toggle = (key: keyof typeof settings) =>
    setSettings((s) => ({ ...s, [key]: !s[key] }));

  const notifItems = [
    { key: 'newBookings', label: 'New Bookings', desc: 'Get notified when a new booking is placed' },
    { key: 'driverAlerts', label: 'Driver Alerts', desc: 'Driver status changes and KYC updates' },
    { key: 'paymentAlerts', label: 'Payment Alerts', desc: 'Withdrawal requests and payment issues' },
    { key: 'kycReviews', label: 'KYC Reviews', desc: 'Pending KYC documents awaiting review' },
    { key: 'systemAlerts', label: 'System Alerts', desc: 'Backend errors and system warnings' },
    { key: 'weeklyReports', label: 'Weekly Reports', desc: 'Automated weekly summary reports' },
  ] as const;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Notification Preferences
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-1">
          {notifItems.map((item) => (
            <div
              key={item.key}
              className="flex items-center justify-between py-3 border-b last:border-0"
            >
              <div>
                <p className="font-medium text-sm">{item.label}</p>
                <p className="text-xs text-muted-foreground">{item.desc}</p>
              </div>
              <Switch
                checked={settings[item.key]}
                onChange={() => toggle(item.key)}
              />
            </div>
          ))}
        </div>
        <div className="mt-4 flex justify-end">
          <Button size="sm">Save Preferences</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function DangerZoneTab() {
  const [resetAllModal, setResetAllModal] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const resetAllMutation = useMutation({
    mutationFn: async () => {
      const { data } = await api.delete(API_ENDPOINTS.DATA.RESET_ALL, {
        data: { confirm: 'RESET_ALL_DATA' },
      });
      return data.data ?? data;
    },
    onSuccess: (result) => {
      toast.success('All driver data has been reset successfully');
      setResetAllModal(false);
      setConfirmText('');
      console.info('[Admin] Reset result:', result);
    },
    onError: () => {
      toast.error('Failed to reset data. Try again.');
    },
  });

  return (
    <div className="space-y-4">
      <Card className="border-destructive/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base text-destructive">
            <Trash2 className="h-5 w-5" />
            Reset All Driver Data
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 rounded-xl border border-destructive/20 bg-destructive/5">
            <div>
              <p className="text-sm font-semibold">Wipe all earnings, trips &amp; wallet balances</p>
              <p className="text-xs text-muted-foreground mt-1 max-w-lg">
                Permanently deletes every booking, payment, wallet transaction, rating, topup, and withdrawal record
                across <strong>all drivers</strong>. Resets wallet balances and trip counters to zero.
                This cannot be undone.
              </p>
            </div>
            <Button
              variant="destructive"
              size="sm"
              className="flex-shrink-0"
              onClick={() => setResetAllModal(true)}
            >
              <Trash2 className="h-4 w-4 mr-1.5" />
              Reset All Data
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Confirmation modal */}
      <Modal isOpen={resetAllModal} onClose={() => { setResetAllModal(false); setConfirmText(''); }}>
        <ModalHeader onClose={() => { setResetAllModal(false); setConfirmText(''); }}>
          <ModalTitle>Reset All Driver Data</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
              <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-destructive">This wipes ALL driver data. It cannot be undone.</p>
                <ul className="mt-2 space-y-0.5 text-muted-foreground list-disc list-inside">
                  <li>All bookings &amp; payment records</li>
                  <li>All wallet balances &amp; transactions</li>
                  <li>All trip counts &amp; total earnings</li>
                  <li>All ratings &amp; reviews</li>
                  <li>All topup &amp; withdrawal history</li>
                </ul>
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Type <strong>RESET</strong> to confirm</label>
              <input
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                placeholder="RESET"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={() => { setResetAllModal(false); setConfirmText(''); }}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => resetAllMutation.mutate()}
            disabled={confirmText !== 'RESET' || resetAllMutation.isPending}
          >
            <Trash2 className="h-4 w-4 mr-1.5" />
            {resetAllMutation.isPending ? 'Resetting...' : 'Reset All Data'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export function SettingsPage() {
  const { user } = useAuthStore();
  const isSuperAdmin = !!user?.is_super_admin;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Settings"
        description="Manage your account settings and preferences"
      />

      <Tabs defaultValue="profile">
        <TabsList className="w-full justify-start">
          <TabsTrigger value="profile" className="flex items-center gap-2">
            <User className="h-4 w-4" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center gap-2">
            <Lock className="h-4 w-4" />
            Security
          </TabsTrigger>
          {isSuperAdmin && (
            <TabsTrigger value="team" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Team
            </TabsTrigger>
          )}
          <TabsTrigger value="notifications" className="flex items-center gap-2">
            <Bell className="h-4 w-4" />
            Notifications
          </TabsTrigger>
          <TabsTrigger value="danger" className="flex items-center gap-2 text-destructive data-[state=active]:text-destructive">
            <Trash2 className="h-4 w-4" />
            Danger Zone
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <ProfileTab />
        </TabsContent>
        <TabsContent value="security">
          <SecurityTab />
        </TabsContent>
        {isSuperAdmin && (
          <TabsContent value="team">
            <TeamManagementTab />
          </TabsContent>
        )}
        <TabsContent value="notifications">
          <NotificationsTab />
        </TabsContent>
        <TabsContent value="danger">
          <DangerZoneTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
